// ============================================
// FICHAPERSONA.JS — Detalle de persona con sus roles
// Una misma persona puede ser cliente persona física Y contacto de empresas.
// ============================================

import React, { useEffect, useState } from 'react';
import PedirDatosCard from './PedirDatosCard';
import KitProveedorCard from './KitProveedorCard';
import GarantiasLista from './GarantiasLista';
import { authHeaders } from '../utils/storage';
import {
  API_BASE_CLI, PP, COLOR_PRINCIPAL, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario, estiloTarjeta,
  CabeceraFicha, TIPOS_ROL, ESTADOS_ROL, AREAS_TOMADOR,
} from './helpersClientes';
import SelectorMapa, { MiniMapa } from './SelectorMapa';
import BotonArchivarCliente from './BotonArchivarCliente';
import SelloAutoria from './SelloAutoria';

const CAMPOS_PERSONA = [
  { k: 'nombre_completo', label: 'Nombre completo', requerido: true },
  { k: 'celular',         label: 'Celular' },
  { k: 'email',           label: 'Email' },
  { k: 'email_alternativo', label: 'Email alternativo' },
  { k: 'cumpleanos',      label: 'Cumpleaños', tipo: 'date' },
];

function celularWhatsAppP(tel) {
  if (!tel) return '';
  const d = String(tel).replace(/\D/g, '');
  if (!d) return '';
  return d.startsWith('52') ? d : ('52' + d);
}

const CAMPOS_PF = [
  { k: 'rfc',       label: 'RFC' },
  { k: 'curp',      label: 'CURP' },
  { k: 'direccion', label: 'Dirección', multiline: true },
];

export default function FichaPersona({ id, onVolver, esCelular }) {
  const [persona, setPersona] = useState(null);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({});
  const [editando, setEditando] = useState(false);
  const [modalRol, setModalRol] = useState(false);
  const [modalMapa, setModalMapa] = useState(false);
  const [cargando, setCargando] = useState(true);

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/clientes/personas/${id}`, { headers: authHeaders() }).then(x => x.json());
      if (r.ok) {
        setPersona(r.persona);
        setRoles(r.roles || []);
        setForm(r.persona);
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, [id]);

  const guardar = async () => {
    const r = await fetch(`${API_BASE_CLI}/api/clientes/personas/${id}`, {
      method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    }).then(x => x.json());
    if (r.ok) setPersona(r.persona);
    setEditando(false);
    cargar();
  };

  const quitarRol = async (rolId) => {
    if (!window.confirm('¿Quitar este rol?')) return;
    await fetch(`${API_BASE_CLI}/api/clientes/roles-contacto/${rolId}`, { method: 'DELETE', headers: { ...authHeaders() } });
    cargar();
  };

  const guardarCoordenadas = async (payload) => {
    const body = {
      latitud: payload.latitud,
      longitud: payload.longitud,
    };
    if (payload.direccion !== undefined) body.direccion = payload.direccion;
    if (payload.colonia !== undefined) body.colonia = payload.colonia;
    if (payload.ciudad !== undefined) body.ciudad = payload.ciudad;
    if (payload.estado !== undefined) body.estado_republica = payload.estado;
    if (payload.codigo_postal !== undefined) body.codigo_postal = payload.codigo_postal;

    const r = await fetch(`${API_BASE_CLI}/api/clientes/personas/${id}`, {
      method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(x => x.json());
    if (r.ok) { setPersona(r.persona); setForm(r.persona); }
    setModalMapa(false);
  };

  if (cargando || !persona) {
    return <div style={{ fontFamily: PP, padding: 40, textAlign: 'center', color: COLOR_GRIS_CLARO }}>Cargando persona…</div>;
  }

  const rolesPorTipo = roles.reduce((acc, r) => {
    (acc[r.tipo] = acc[r.tipo] || []).push(r);
    return acc;
  }, {});

  return (
    <div style={{ fontFamily: PP, background: COLOR_FONDO, minHeight: '100%', color: COLOR_GRIS }}>
      <CabeceraFicha
        volver={onVolver}
        titulo={persona.nombre_completo}
        subtitulo={[
          persona.es_cliente_persona_fisica ? 'Cliente persona física' : null,
          `${roles.length} rol${roles.length === 1 ? '' : 'es'}`,
        ].filter(Boolean).join(' · ')}
        estado={persona.estado}
        acciones={
          editando
            ? (
              <>
                <button style={estiloBotonSecundario} onClick={() => { setEditando(false); setForm(persona); }}>Cancelar</button>
                <button style={{ ...estiloBotonPrimario, marginLeft: 8 }} onClick={guardar}>Guardar</button>
              </>
            )
            : <button style={estiloBotonSecundario} onClick={() => setEditando(true)}>Editar</button>
        }
      />

      <div style={{ padding: esCelular ? 16 : 24, display: 'grid', gap: 16 }}>
        {/* Acciones rápidas */}
        <AccionesRapidas persona={persona} />

        {/* Datos personales */}
        <div style={estiloTarjeta}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 14 }}>Datos personales</div>
          <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: 14 }}>
            {CAMPOS_PERSONA.map(({ k, label, tipo, requerido }) => (
              <div key={k}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLOR_GRIS_CLARO }}>{label}</label>
                {editando ? (
                  <input
                    type={tipo || 'text'}
                    style={estiloInput}
                    value={form[k] || ''}
                    onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))}
                  />
                ) : (
                  <div style={{ fontSize: 13, color: persona[k] ? COLOR_GRIS : COLOR_GRIS_CLARO, marginTop: 4 }}>
                    {persona[k] || '— Sin especificar —'}
                  </div>
                )}
              </div>
            ))}
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: COLOR_GRIS_CLARO }}>¿Es cliente persona física?</label>
              {editando ? (
                <select
                  style={estiloInput}
                  value={form.es_cliente_persona_fisica ? 'si' : 'no'}
                  onChange={(e) => setForm(f => ({ ...f, es_cliente_persona_fisica: e.target.value === 'si' }))}
                >
                  <option value="no">No</option>
                  <option value="si">Sí</option>
                </select>
              ) : (
                <div style={{ fontSize: 13, marginTop: 4 }}>{persona.es_cliente_persona_fisica ? 'Sí' : 'No'}</div>
              )}
            </div>
          </div>
        </div>

        {/* Datos fiscales — solo si es cliente persona física */}
        {(persona.es_cliente_persona_fisica || (editando && form.es_cliente_persona_fisica)) && (
          <div style={estiloTarjeta}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 14 }}>Datos fiscales (cliente persona física)</div>
            <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: 14 }}>
              {CAMPOS_PF.map(({ k, label, multiline }) => (
                <div key={k} style={{ gridColumn: multiline ? (esCelular ? 'auto' : '1 / -1') : 'auto' }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: COLOR_GRIS_CLARO }}>{label}</label>
                  {editando ? (
                    multiline ? (
                      <textarea style={{ ...estiloInput, minHeight: 60 }} value={form[k] || ''} onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))} />
                    ) : (
                      <input style={estiloInput} value={form[k] || ''} onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))} />
                    )
                  ) : (
                    <div style={{ fontSize: 13, color: persona[k] ? COLOR_GRIS : COLOR_GRIS_CLARO, marginTop: 4, whiteSpace: 'pre-wrap' }}>
                      {persona[k] || '— Sin especificar —'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ubicación */}
        <UbicacionPersona
          persona={persona}
          onAbrirMapa={() => setModalMapa(true)}
        />

        {/* Roles */}
        <div style={estiloTarjeta}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO }}>Roles en empresas</div>
            <button style={estiloBotonPrimario} onClick={() => setModalRol(true)}>+ Agregar rol</button>
          </div>
          {roles.length === 0 && (
            <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, padding: '8px 0' }}>Sin roles asignados.</div>
          )}
          {Object.entries(rolesPorTipo).map(([tipo, lista]) => {
            const def = TIPOS_ROL[tipo] || { label: tipo, color: '#999' };
            return (
              <div key={tipo} style={{ marginBottom: 14 }}>
                <div style={{
                  display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '3px 8px',
                  background: def.color + '20', color: def.color, borderRadius: 4,
                  textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8,
                }}>
                  {def.label}
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {lista.map(r => {
                    const est = ESTADOS_ROL[r.estado_rol] || ESTADOS_ROL.activo_confirmado;
                    const area = r.area ? (AREAS_TOMADOR.find(a => a.id === r.area)?.label || r.area) : null;
                    return (
                      <div key={r.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                        background: COLOR_FONDO, borderRadius: 8,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>
                            {r.alcance_tipo.replace('_', ' ')} · ID {r.alcance_id}
                          </div>
                          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
                            {[r.puesto, area].filter(Boolean).join(' · ')}
                            {r.email_corporativo && ` · ${r.email_corporativo}`}
                          </div>
                        </div>
                        <span title={est.label} style={{ width: 8, height: 8, borderRadius: '50%', background: est.color }} />
                        <button
                          onClick={() => quitarRol(r.id)}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: COLOR_GRIS_CLARO, fontSize: 14 }}
                          title="Quitar"
                        >✕</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Notas */}
        <NotasPersona
          persona={persona}
          onGuardado={(nuevo) => setPersona(p => ({ ...p, notas: nuevo }))}
        />

        {/* Historial personal (placeholder) */}
        <div style={estiloTarjeta}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>Historial</div>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Se integrará en FASE 2 con interacciones y reportes.</div>
        </div>
        {/* GARANTIAS (2026-09-07): certificados de este cliente, registro oficial. */}
        <div style={estiloTarjeta}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>Garantías</div>
          <GarantiasLista filtro={{ persona_id: id }} compacto />
        </div>
        {/* KIT DE PROVEEDOR (2026-09-08): lo que Beep entrega para darse de alta con este cliente. */}
        <KitProveedorCard cliente={{ persona_id: id, nombre: persona.nombre_completo, correo: persona.email || '' }} />
        {/* PORTAL DEL CLIENTE (2026-09-08): pedirle sus datos con un enlace temporal. */}
        <PedirDatosCard cliente={{ persona_id: id, nombre: persona.nombre_completo }} />
      </div>

      {modalRol && (
        <ModalAgregarRol
          personaId={id}
          onCerrar={() => setModalRol(false)}
          onCreado={() => { setModalRol(false); cargar(); }}
        />
      )}
      {modalMapa && (
        <SelectorMapa
          latitudInicial={persona.latitud}
          longitudInicial={persona.longitud}
          onGuardar={guardarCoordenadas}
          onCancelar={() => setModalMapa(false)}
          titulo={`📍 Ubicación de ${persona.nombre_completo}`}
        />
      )}

      {/* AUTORIA-CLIENTES (2026-09-18): quien lo creo y quien lo toco por ultima vez.
          Lo anterior a la migracion 087 sale como "desconocido": no se le atribuye a nadie. */}
      <div style={{ padding: esCelular ? '0 16px' : '0 24px' }}>
        <SelloAutoria entidad={persona} />
      </div>

      {/* Acciones admin discretas — solo Dirección puede archivar */}
      <div style={{ padding: esCelular ? '20px 16px 32px' : '24px 24px 40px', display: 'flex', justifyContent: 'flex-end' }}>
        <BotonArchivarCliente
          tipo="persona" id={persona.id} entidad={persona}
          onArchivado={onVolver}
        />
      </div>
    </div>
  );
}

function UbicacionPersona({ persona, onAbrirMapa }) {
  const tieneCoord = persona.latitud != null && persona.longitud != null;
  const lat = tieneCoord ? Number(persona.latitud) : null;
  const lng = tieneCoord ? Number(persona.longitud) : null;

  return (
    <div style={estiloTarjeta}>
      <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 14 }}>📍 Ubicación</div>
      {!tieneCoord && (
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 12 }}>
          Esta persona aún no tiene ubicación registrada.
        </div>
      )}
      {tieneCoord && (
        <div style={{ marginBottom: 12 }}>
          <MiniMapa lat={lat} lng={lng} alto={220} />
          <div style={{ fontSize: 12, color: COLOR_GRIS, marginTop: 8 }}>
            Coordenadas: <strong>{lat.toFixed(6)}, {lng.toFixed(6)}</strong>
          </div>
          <a
            href={`https://www.google.com/maps/?q=${lat},${lng}`}
            target="_blank" rel="noreferrer"
            style={{
              display: 'inline-block', marginTop: 6, padding: '6px 12px',
              border: `1px solid ${COLOR_BORDE}`, borderRadius: 6, fontSize: 12,
              color: COLOR_GRIS, textDecoration: 'none', fontFamily: PP,
            }}
          >
            🗺️ Abrir en Google Maps
          </a>
        </div>
      )}
      <button style={estiloBotonPrimario} onClick={onAbrirMapa}>
        {tieneCoord ? 'Cambiar ubicación' : 'Capturar ubicación'}
      </button>
    </div>
  );
}

function AccionesRapidas({ persona }) {
  if (!persona) return null;
  const celWA = celularWhatsAppP(persona.celular);
  const copiarTel = () => {
    if (!persona.celular) return;
    try { navigator.clipboard.writeText(persona.celular); } catch { /* no-op */ }
  };
  const hayAlgo = persona.celular || persona.email;
  if (!hayAlgo) return null;
  return (
    <div style={{
      ...estiloTarjeta, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%', background: COLOR_PRINCIPAL,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: 22, fontWeight: 700, flexShrink: 0,
      }}>
        {persona.nombre_completo ? persona.nombre_completo[0].toUpperCase() : '·'}
      </div>
      <div style={{ flex: 1, minWidth: 140 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_OSCURO }}>{persona.nombre_completo}</div>
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
          {persona.celular || persona.email || ''}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {persona.celular && (
          <>
            <a href={`tel:${persona.celular}`} title="Llamar" style={btnAccion}>📞 Llamar</a>
            {celWA && (
              <a href={`https://wa.me/${celWA}`} target="_blank" rel="noreferrer" title="WhatsApp" style={btnAccion}>💬 WhatsApp</a>
            )}
            <button onClick={copiarTel} title="Copiar tel" style={btnAccion}>📋 Copiar</button>
          </>
        )}
        {persona.email && (
          <a href={`mailto:${persona.email}`} title="Email" style={btnAccion}>✉️ Email</a>
        )}
      </div>
    </div>
  );
}

const btnAccion = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '8px 12px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
  background: '#fff', fontSize: 12, color: COLOR_OSCURO, fontWeight: 600,
  cursor: 'pointer', textDecoration: 'none', fontFamily: PP,
};

function NotasPersona({ persona, onGuardado }) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(persona.notas || '');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { setValor(persona.notas || ''); }, [persona.notas]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/clientes/personas/${persona.id}`, {
        method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ notas: valor }),
      }).then(x => x.json());
      if (r.ok) { onGuardado(valor); setEditando(false); }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={estiloTarjeta}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO }}>📝 Notas</div>
        {!editando && (
          <button style={estiloBotonSecundario} onClick={() => setEditando(true)}>
            {persona.notas ? 'Editar' : '+ Agregar'}
          </button>
        )}
      </div>
      {editando ? (
        <div>
          <textarea
            style={{ ...estiloInput, minHeight: 80, resize: 'vertical' }}
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            autoFocus
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <button style={estiloBotonSecundario} onClick={() => { setValor(persona.notas || ''); setEditando(false); }}>Cancelar</button>
            <button style={estiloBotonPrimario} onClick={guardar} disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 13, color: persona.notas ? COLOR_GRIS : COLOR_GRIS_CLARO, whiteSpace: 'pre-wrap' }}>
          {persona.notas || '— Sin notas —'}
        </div>
      )}
    </div>
  );
}

function ModalAgregarRol({ personaId, onCerrar, onCreado }) {
  const [tipo, setTipo] = useState('administrador');
  const [alcanceTipo, setAlcanceTipo] = useState('razon_social');
  const [alcanceId, setAlcanceId] = useState('');
  const [area, setArea] = useState('');
  const [puesto, setPuesto] = useState('');
  const [opciones, setOpciones] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const url = {
      grupo:         '/api/clientes/grupos',
      razon_social:  '/api/clientes/razones-sociales',
      sucursal:      '/api/clientes/sucursales',
      zona:          '/api/clientes/zonas',
    }[alcanceTipo];
    fetch(`${API_BASE_CLI}${url}`, { headers: authHeaders() }).then(x => x.json()).then(j => {
      const k = { grupo: 'grupos', razon_social: 'razones_sociales', sucursal: 'sucursales', zona: 'zonas' }[alcanceTipo];
      const label = {
        grupo: 'nombre', razon_social: 'razon_social', sucursal: 'nombre_interno', zona: 'nombre',
      }[alcanceTipo];
      setOpciones((j[k] || []).map(x => ({ id: x.id, label: x[label] })));
    });
    setAlcanceId('');
  }, [alcanceTipo]);

  const guardar = async () => {
    if (!alcanceId) { setError('Selecciona a qué aplica'); return; }
    setGuardando(true); setError(null);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/clientes/roles-contacto`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          persona_id: personaId, tipo,
          area: tipo === 'administrador' ? area || null : null,
          puesto: puesto || null,
          alcance_tipo: alcanceTipo, alcance_id: alcanceId,
        }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error);
      onCreado();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.45)', zIndex: 1050,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onCerrar}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 460, fontFamily: PP,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 16 }}>Agregar rol</div>

        <label style={{ fontSize: 12, fontWeight: 600 }}>Tipo de rol *</label>
        <select style={estiloInput} value={tipo} onChange={(e) => setTipo(e.target.value)}>
          {Object.entries(TIPOS_ROL).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>

        {tipo === 'administrador' && (
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Área</label>
            <select style={estiloInput} value={area} onChange={(e) => setArea(e.target.value)}>
              <option value="">— Sin especificar —</option>
              {AREAS_TOMADOR.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Alcance *</label>
          <select style={estiloInput} value={alcanceTipo} onChange={(e) => setAlcanceTipo(e.target.value)}>
            <option value="grupo">Grupo (cliente)</option>
            <option value="razon_social">Razón social</option>
            <option value="zona">Zona</option>
            <option value="sucursal">Sucursal</option>
          </select>
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Aplica a *</label>
          <select style={estiloInput} value={alcanceId} onChange={(e) => setAlcanceId(e.target.value)}>
            <option value="">— Selecciona —</option>
            {opciones.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Puesto (opcional)</label>
          <input style={estiloInput} value={puesto} onChange={(e) => setPuesto(e.target.value)} />
        </div>

        {error && <div style={{ color: '#e05c5c', fontSize: 12, marginTop: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button style={estiloBotonSecundario} onClick={onCerrar}>Cancelar</button>
          <button style={estiloBotonPrimario} onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
